/**
 * Les sept info-bulles de première partie (phase 4, v0.37.0 — A78, A81) :
 * identifiants, écrans, ancres et textes. Données pures, sans DOM.
 *
 * **L'ordre du tableau est l'ordre de priorité** : si plusieurs bulles ont leur
 * condition vraie au même montage d'écran, la première de la liste s'affiche.
 * Les conditions elles-mêmes vivent dans `ui/tips.ts` (`tipApplies`).
 */

export type TipId = 'throw' | 'recap' | 'tree' | 'shop' | 'equip' | 'invasion' | 'early';

/** Écran qui porte la bulle : le combat (HUD), la fin d'Expédition ou l'Accalmie. */
export type TipScreen = 'combat' | 'expeditionEnd' | 'accalmie';

export interface TipDef {
  id: TipId;
  screen: TipScreen;
  /** Texte exact d'A81. */
  text: string;
  /** Sélecteur CSS de l'ancre (la flèche la désigne). */
  anchor: string;
  /**
   * Ancre plus précise, préférée quand elle est visible (A100-3) : l'onglet Dés
   * est déjà ouvert, la flèche vise alors son bon sous-onglet plutôt que l'onglet.
   */
  anchorInside?: string;
  /**
   * Où chercher l'ancre : `screen` = dans l'élément de l'écran ; `app` = dans
   * toute la colonne (l'invite de lancer vit dans la couche 3D, hors du HUD).
   */
  scope: 'screen' | 'app';
  /** Côté préféré de la bulle ; l'autre est pris si la place manque. */
  prefer: 'above' | 'below';
}

export const TIPS: readonly TipDef[] = [
  {
    id: 'throw',
    screen: 'combat',
    text: 'Glissez vers le haut ou touchez l\'écran pour lancer vos dés',
    anchor: '[data-role="throw-hint"]',
    scope: 'app',
    prefer: 'above',
  },
  {
    id: 'recap',
    screen: 'expeditionEnd',
    text: 'Touchez un lancer du récapitulatif pour voir le détail du calcul',
    anchor: '[data-role="damage-recap"] > summary',
    scope: 'screen',
    prefer: 'below',
  },
  {
    id: 'tree',
    screen: 'accalmie',
    text: 'Dépensez votre or dans l\'Arbre des talents',
    anchor: '[data-tab="tree"]',
    scope: 'screen',
    prefer: 'below',
  },
  {
    id: 'shop',
    screen: 'accalmie',
    text: 'Les GachaDice fabriquent des dés Maîtres',
    anchor: '[data-tab="shop"]',
    scope: 'screen',
    prefer: 'below',
  },
  {
    id: 'equip',
    screen: 'accalmie',
    text: 'Équipez vos dés Maîtres dans les emplacements',
    anchor: '[data-tab="dice"]',
    // Les emplacements vivent dans le sous-onglet « Équipés » de l'onglet Dés (A100-3).
    anchorInside: '[data-role="dice-subtab"][data-subtab="equipped"]',
    scope: 'screen',
    prefer: 'below',
  },
  {
    id: 'invasion',
    screen: 'accalmie',
    text: 'Jour 5 terminé : l\'Invasion vous attend, quatre monstres puis le boss',
    anchor: '[data-action="next"]',
    scope: 'screen',
    prefer: 'above',
  },
  {
    id: 'early',
    screen: 'accalmie',
    text: 'Vous vous sentez fort ? « Aller au-devant » déclenche l\'Invasion sans attendre',
    anchor: '[data-action="early"]',
    scope: 'screen',
    prefer: 'below',
  },
];

export const TIP_IDS: readonly TipId[] = TIPS.map((t) => t.id);

export function tipDef(id: TipId): TipDef {
  return TIPS.find((t) => t.id === id) as TipDef;
}
